/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import images from 'react-payment-inputs/images'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Anchor, Button, Badge, Card } from '@nexigendigital/nxui'
import { useAccountPaymentMethodsQuery } from 'api/billing'
import { PaymentMethodData } from 'models/billing'
import { parseISO } from 'helpers/utils'

import { AddPaymentMethodLightbox } from 'containers/billing/lightbox/addPaymentMethodLightbox'
import { RemovePaymentMethodLightbox } from 'containers/billing/lightbox/removePaymentMethodLightbox'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function PaymentMethodsContainer() {
    /*   STATE
    **********************************************************************************************************/
    const [paymentMethods, setPaymentMethods] = useState([])
    const [lightbox, setLightbox] = useState({
        status: false,
        template: '',
        meta: {
            id: ''
        }
    })

    /*   QUERIES & MUTATIONS
    **********************************************************************************************************/
    const {
        data: paymentMethodsData = [],
        isFetching: paymentMethodsLoading,
        isSuccess: paymentMethodsSuccess
    } = useAccountPaymentMethodsQuery(undefined)

    /*   EFFECTS
    **********************************************************************************************************/
    useEffect(() => {
        if (paymentMethodsSuccess) {
            // @ts-ignore
            setPaymentMethods(paymentMethodsData.map(({ type, id, identifier, expiry, is_default }: PaymentMethodData) => {
                return {
                    type: <svg { ...images[type].props } width={'25'} height={'16'}/>,
                    number: `**** **** **** ${identifier}`,
                    expiry: `${parseISO(expiry).month}/${parseISO(expiry).year}`,
                    status: is_default && <Badge color={'primary'}>Default</Badge>,
                    action: (
                        <Anchor
                            color={'error'}
                            onClick={() => setLightbox({ status: true, template: 'remove', meta: { id: id } })}
                        >
                            Remove
                        </Anchor>
                    )
                }
            }))
        }
    }, [paymentMethodsData, paymentMethodsSuccess, setPaymentMethods])

    const columns = useMemo(() => [
        {
            Header: '',
            accessor: 'type'
        }, {
            Header: 'Card Number',
            accessor: 'number'
        }, {
            Header: 'Expiry',
            accessor: 'expiry'
        }, {
            Header: 'Status',
            accessor: 'status'
        }, {
            Header: '',
            accessor: 'action',
            align: 'right'
        }
    ], [])

    return (
        <>
            <PaymentMethods.Card
                table={{
                    conditions: {
                        loading: paymentMethodsLoading && 'Loading Payment Methods...'
                    },
                    render: {
                        columns: () => columns,
                        data: () => paymentMethods
                    }
                }}
                footer={(
                    <PaymentMethods.Footer>
                        <Button
                            type={'button'}
                            color={'primary'}
                            onClick={() => setLightbox({ ...lightbox, status: true, template: 'add' })}
                        >
                            Add New Payment Method
                        </Button>
                    </PaymentMethods.Footer>
                )}
            />
            <AddPaymentMethodLightbox
                status={lightbox.status && lightbox.template === 'add'}
                close={() => setLightbox({ status: false, template: '', meta: { id: '' } })}
            />
            <RemovePaymentMethodLightbox
                id={lightbox.meta?.id}
                status={lightbox.status && lightbox.template === 'remove'}
                close={() => setLightbox({ status: false, template: '', meta: { id: '' } })}
            />
        </>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const PaymentMethods = {
    Card: styled(Card)`
        width: 100%;
        margin-bottom: 0;
        border-radius: 0 0 16px 16px;

        > header {
            border-radius: 0;
        }
    `,
    Footer: styled.div`
        display: flex;
        justify-content: flex-end;
        padding: 10px 10px 10px 0;
        border-top: 1px solid ${props => (props.theme['gray--100'])};
    `
}

export { PaymentMethodsContainer as PaymentMethods }
